import datetimeDecorator from "src/decorators/datetime.decorator";
import {
  ShipmentOrderAmountEntityBase,
  ShipmentOrderBillingAmountEntity,
  ShipmentOrderLegRunPaymentAmountEntity,
  ShipmentOrderResponseEntity,
} from "src/entities/ShipmentOrderResponse.entity";

export interface ShipmentLegRunItem {
  id: number | string;
  shipmentId: number | string;
  sectionType: string;
  deliveryMethod: string;
  shipperName: string;
  route: string;
  loadingAddress: string;
  unloadingAddress: string;
  loadingOn: string;
  unloadingOn: string;
  loadingTimeWindow: string;
  unloadingTimeWindow: string;
  loadingStayingMinutes: number;
  unloadingStayingMinutes: number;
  quantity: number;
  weightKg: number | "";
  volumeM3: number | "";
  paymentAmountTotal: number;
  billingAmountTotal: number;
  dispatchStatus: string;
  carrierCompany: string | null;
  useLtl?: boolean;
  operatingCarrier: string | null;
  truckNumber: string | null;
  driverName: string | null;
  hierarchyLevel: number | string | null;
  planningLicenseName: string | null;
  paymentAmount: ShipmentOrderLegRunPaymentAmountEntity | null;
  billingAmount: ShipmentOrderBillingAmountEntity | null;
}

export const getEarliestDateFormatted = (
  times: { startAt: string; endAt: string }[]
): string => {
  if (!times?.length) return "";

  const sortedTimes = [...times].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
  );

  return datetimeDecorator.toYyyyMmDd(new Date(sortedTimes[0].startAt));
};

const formatTimeWindow = (times: { startAt: string; endAt: string }[]): string => {
  if (!times?.length) return "";

  return times
    .map((time) => {
      const start = datetimeDecorator.toHourMinutes(new Date(time.startAt));
      const end = datetimeDecorator.toHourMinutes(new Date(time.endAt));
      return `${start}-${end}`;
    })
    .join(", ");
};

/**
 * 金額の合計。請求と支払で項目が同じなので 1 つの関数で数える。
 * 請求だけ別実装にしていた名残で、行に請求を載せ忘れていた（指摘 2026/08/19）。
 */
const calculateAmountTotal = (
  amount: ShipmentOrderAmountEntityBase | null,
): number => {
  if (!amount) return 0;

  return [
    amount.basicFeeYen || 0,
    amount.ancillaryFeeYen || 0,
    amount.expensesFeeYen || 0,
    amount.highwayFeeYen || 0,
    amount.loadingFeeYen || 0,
    amount.relayFeeYen || 0,
    amount.surchargeFeeYen || 0,
    amount.unloadingFeeYen || 0,
    amount.waitingTimeFeeYen || 0,
  ]
    .filter((fee) => fee)
    .reduce((acc, fee) => acc + Number(fee), 0);
};

/** 未登録なら空の金額を返す。編集欄が undefined を掴まないようにする */
const ensureAmount = <T extends ShipmentOrderAmountEntityBase>(amount: T | null): T => {
  if (amount) return amount;

  return {
    basicFeeYen: null,
    ancillaryFeeYen: null,
    expensesFeeYen: null,
    highwayFeeYen: null,
    loadingFeeYen: null,
    relayFeeYen: null,
    surchargeFeeYen: null,
    unloadingFeeYen: null,
    waitingTimeFeeYen: null,
    ancillaryContent: "",
  } as T;
};


export const buildShipmentLegRunItems = (
  shipmentData: ShipmentOrderResponseEntity
): ShipmentLegRunItem[] => {
  const loadingWaypoint = shipmentData.shipmentOrderLoadingWaypoint;
  const unloadingWaypoint = shipmentData.shipmentOrderUnloadingWaypoint;

  const loadingTimes =
    loadingWaypoint.shipmentOrderWaypointLoadingTimes ?? [];
  const unloadingTimes =
    unloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? [];

  const loadingOn = getEarliestDateFormatted(loadingTimes);
  const unloadingOn = getEarliestDateFormatted(unloadingTimes);
  const loadingTimeWindow = formatTimeWindow(loadingTimes);
  const unloadingTimeWindow = formatTimeWindow(unloadingTimes);

  return shipmentData.shipmentOrderLegs.flatMap((leg) =>
    leg.shipmentOrderLegRuns.map((run) => {
      const quantity = run.items.reduce(
        (acc, item) => acc + (item.quantity || 0),
        0
      );
      const weightKg = run.items.reduce(
        (acc, item) => acc + (item.weightKg || 0),
        0
      );
      const volumeM3 = run.items.reduce(
        (acc, item) => acc + (item.volumeM3 || 0),
        0
      );
      const paymentAmount = ensureAmount<ShipmentOrderLegRunPaymentAmountEntity>(run.paymentAmount);
      // 請求も同じ行に出す。支払だけ詰めていたため、保存した請求が開き直すと空欄に見えた
      const billingAmount = ensureAmount<ShipmentOrderBillingAmountEntity>(run.billingAmount);

      return {
        id: run.id,
        shipmentId: shipmentData.id,
        sectionType: leg.stats,
        deliveryMethod: shipmentData.deliveryMethod,
        shipperName: shipmentData.shipperName,
        route: `${loadingWaypoint.name} → ${unloadingWaypoint.name}`,
        loadingAddress: loadingWaypoint.address,
        unloadingAddress: unloadingWaypoint.address,
        loadingOn,
        unloadingOn,
        loadingTimeWindow,
        unloadingTimeWindow,
        loadingStayingMinutes: loadingWaypoint.stayingMinutes as number,
        unloadingStayingMinutes: unloadingWaypoint.stayingMinutes as number,
        quantity,
        weightKg,
        volumeM3,
        paymentAmountTotal: calculateAmountTotal(paymentAmount),
        billingAmountTotal: calculateAmountTotal(billingAmount),
        dispatchStatus: run.planningLicenseId ? "配車済" : "未配車",
        carrierCompany: run.carrierCompany,
        operatingCarrier: run.operatingCarrier,
        truckNumber: run.truckNumber,
        driverName: run.driverName,
        hierarchyLevel: run.hierarchyLevel,
        planningLicenseName: run.planningLicenseName,
        paymentAmount,
        billingAmount,
      };
    })
  );
};
